import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { NAV_CTA, NAV_ITEMS, NAV_LOGIN } from '../content/nav';
import Button from '../primitives/Button';
import Wordmark from '../primitives/Wordmark';
import MobileMenu from './MobileMenu';
import { useNavMode } from './NavModeContext';

export type ActivePage = 'home' | 'about' | 'membership' | 'assessments' | 'faq';

interface NavProps {
  activePage: ActivePage;
  /** Focused flows (enrolment): wordmark only. */
  minimal: boolean;
  /** PUBLIC_SITE=hero: the About/Membership routes redirect, so their links go. */
  heroOnly: boolean;
}

const MENU_ID = 'mk-mobile-menu';

/**
 * The fixed nav from the mockups (brand/GUIDELINES.md §7): primary serif
 * wordmark left; ABOUT · MEMBERSHIP, LOG IN and an outlined call to action
 * right, all in details caps. Transparent and white while the hero is under
 * it, Bone and black otherwise. No scroll-stow: hiding the nav on scroll is
 * motion for its own sake and hides the call to action.
 */
export default function Nav({ activePage, minimal, heroOnly }: NavProps) {
  // Held in the nav context, not here, so overlays can stay clear of the menu.
  const { onPhoto, landing, transitioning, menuOpen, setMenuOpen } = useNavMode();
  const [settled, setSettled] = useState(false);
  const { pathname } = useLocation();

  // A route change (a menu link) closes the menu.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname, setMenuOpen]);

  // The background/colour transition is for scrolling, not mounting. On a
  // fresh mount the hero flips the nav to on-photo before first paint, and
  // animating that flip painted a Bone band across the photograph.
  useEffect(() => {
    const id = setTimeout(() => setSettled(true), 400);
    return () => clearTimeout(id);
  }, []);

  const items = NAV_ITEMS.filter((item) => !item.hidden && !(heroOnly && item.heroHidden));

  return (
    <header
      className="mk-nav"
      data-on-photo={onPhoto && !menuOpen ? 'true' : 'false'}
      data-landing={landing && !menuOpen ? 'true' : 'false'}
      data-settled={settled ? 'true' : 'false'}
      data-transitioning={transitioning ? 'true' : 'false'}
    >
      <div className="mk-container mk-nav__inner">
        <Link to="/" className="mk-nav__brand">
          <Wordmark title="Melio" />
        </Link>

        {!minimal && (
          <>
            <nav className="mk-nav__links" aria-label="Primary">
              {items.filter((item) => !item.mobileOnly).map((item) => (
                <Link
                  key={item.key}
                  to={item.to}
                  className="mk-nav__link t-details"
                  aria-current={activePage === item.key ? 'page' : undefined}
                >
                  {item.label}
                </Link>
              ))}
              <Link to={NAV_LOGIN.to} className="mk-nav__link t-details">
                {NAV_LOGIN.label}
              </Link>
              <Button variant="outline" size="small" to={NAV_CTA.to} className="mk-nav__cta">
                {NAV_CTA.label}
              </Button>
            </nav>

            <button
              type="button"
              className="mk-nav__menu-btn t-details"
              aria-expanded={menuOpen}
              aria-controls={MENU_ID}
              onClick={() => setMenuOpen(true)}
            >
              Menu
            </button>

            {menuOpen && (
              <MobileMenu id={MENU_ID} items={items} activePage={activePage} onClose={() => setMenuOpen(false)} />
            )}
          </>
        )}
      </div>
    </header>
  );
}
